// TrustCenterValuesInAction.jsx
import { useTranslation } from 'react-i18next';

import UnifiedIcon from '../../../components/UnifiedIcon';
import IconHoverText from '../../../components/icon-hover-text/IconHoverText';
import { htmlToPlainText } from '@/views/settings/company/core/htmlToPlainText';

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

export default function TrustCenterValuesInAction({ isLoading, values, colorHex }) {
  const { t } = useTranslation(['trust-center']);
  const hasValues = Array.isArray(values) && values.length > 0;

  if (!isLoading && !hasValues) {
    return null;
  }

  return (
    <div className="mt-10">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          {t('trust-center.values.title')}
          <IconHoverText
            text={t('trust-center.values.subtitle')}
            icon={
              <UnifiedIcon name="Info" library="phosphor" className="w-4 h-4 text-gray-400" />
            }
            width={4}
            height={4}
          />
        </h2>
      </div>

      <div className="mt-4 bg-white rounded-kcWidgets border border-gray-100 shadow-sm">
        {isLoading ? (
          <div className="divide-y divide-gray-100 animate-pulse">
            {Array.from({ length: 3 }).map((_, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div
                key={idx}
                className="p-4 sm:p-6 flex flex-col gap-2 sm:grid sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)_auto] sm:gap-4 items-start sm:items-center"
              >
                <div className="space-y-2 w-full">
                  <div className="h-4 w-32 rounded-full bg-gray-100" />
                  <div className="h-3 w-24 rounded-full bg-gray-100" />
                </div>
                <div className="flex items-center gap-3 w-full sm:col-start-2 sm:col-end-3">
                  <div className="flex-1 h-2 rounded-full bg-gray-100" />
                  <div className="w-14 h-4 rounded-full bg-gray-100 sm:hidden" />
                </div>
                <div className="hidden sm:block w-14 h-4 rounded-full bg-gray-100 justify-self-end" />
              </div>
            ))}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {values.map((v) => (
              <div
                key={v.key}
                className="p-4 sm:p-6 flex flex-col gap-2 sm:grid sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)_auto] sm:gap-4 items-start sm:items-center"
              >
                <div className="flex flex-col items-start text-left w-full">
                  <div className="font-medium text-gray-900">{v.label}</div>
                  <div className="text-xs text-gray-500 line-clamp-2">
                    {htmlToPlainText(v.description)}
                  </div>
                </div>
                {/* Bar + % inline on mobile; bar column on desktop */}
                <div className="flex items-center gap-3 w-full sm:px-12 sm:col-start-2 sm:col-end-3">
                  <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-2 rounded-full"
                      style={{
                        width: `${clamp(v.livedScorePercent ?? 0, 0, 100)}%`,
                        backgroundColor: colorHex || '#14b8a6',
                      }}
                    />
                  </div>
                  <div className="sm:hidden text-sm font-semibold text-gray-900">
                    {typeof v.livedScorePercent === 'number' ? `${v.livedScorePercent}%` : '—'}
                  </div>
                </div>
                {/* Desktop % in its own column */}
                <div className="hidden sm:block w-14 text-right text-sm font-semibold text-gray-900 justify-self-end">
                  {typeof v.livedScorePercent === 'number' ? `${v.livedScorePercent}%` : '—'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

